import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, EyeOff } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-bold text-[#0B2B62]">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function TermoConfidencialidade() {
  usePageTitle(
    "Termo de Confidencialidade",
    "Compromisso de sigilo da RAF Direct sobre dados clínicos, informações sensíveis de saúde e conteúdo de consultas — em conformidade com LGPD Art. 11 e sigilo profissional."
  );
  return (
    <div className="bg-white" data-testid="confidentiality-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-emerald-700">
          <ShieldCheck className="h-3.5 w-3.5" /> Documento legal · Sigilo clínico
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">Termo de Confidencialidade</h1>
        <p className="mt-3 text-sm text-slate-500">
          Última atualização: 05 de fevereiro de 2026 · LGPD Art. 11 (dados sensíveis de saúde)
        </p>

        <div className="mt-6 p-5 rounded-2xl border border-emerald-200 bg-emerald-50/50">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900 leading-relaxed">
              A RAF Direct trata todos os dados de saúde, triagem e conteúdo de consultas como
              <strong> informação estritamente confidencial</strong>. Este termo formaliza nosso compromisso
              de sigilo perante você e seus familiares.
            </p>
          </div>
        </div>

        <Section title="1. Objeto e alcance">
          <p>
            Este Termo de Confidencialidade regula o tratamento de <strong>dados pessoais sensíveis</strong>
            (Art. 5º, II da LGPD) coletados pela RAF Direct em decorrência da prestação dos serviços de
            orientação, triagem inteligente, encaminhamento e realização de consultas por vídeo. Compreende,
            sem se limitar:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Dados de saúde física e mental do paciente;</li>
            <li>Histórico de tratamentos anteriores, medicações e diagnósticos;</li>
            <li>Substância(s) envolvidas, contexto de uso, risco e vulnerabilidade;</li>
            <li>Áudio, vídeo, transcrição e resumo de consultas realizadas na plataforma;</li>
            <li>Documentos anexados (laudos, atestados, receitas, exames);</li>
            <li>Comunicações trocadas via chat, WhatsApp, SMS ou e-mail no âmbito do caso.</li>
          </ul>
        </Section>

        <Section title="2. Compromisso de sigilo">
          <p>
            A RAF Direct, seus colaboradores, consultores, clínicas e profissionais credenciados se
            comprometem a <strong>manter em sigilo absoluto</strong> todas as informações classificadas
            neste termo, utilizando-as exclusivamente para as finalidades do atendimento e nos limites da
            autorização concedida pelo titular.
          </p>
          <p>
            O dever de sigilo <strong>subsiste mesmo após o encerramento do vínculo</strong> entre o
            titular e a plataforma, na forma da legislação aplicável e dos códigos de ética profissional
            (CFM, CFP, COREN e demais conselhos).
          </p>
        </Section>

        <Section title="3. Fundamento legal">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>LGPD (Lei 13.709/2018)</strong>, Art. 11 — tratamento de dados sensíveis;</li>
            <li><strong>Código de Ética Médica</strong> (Resolução CFM 2.217/2018), Cap. IX;</li>
            <li><strong>Código de Ética Profissional do Psicólogo</strong> (Resolução CFP 010/2005), Art. 9º;</li>
            <li><strong>Marco Civil da Internet</strong> (Lei 12.965/2014);</li>
            <li><strong>Código de Defesa do Consumidor</strong> (Lei 8.078/1990).</li>
          </ul>
        </Section>

        <Section title="4. Acesso restrito e segregação por perfil">
          <p>
            O acesso aos dados sensíveis segue o princípio da <strong>necessidade mínima</strong>
            (data minimization) e é segregado por perfil:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Consultor</strong> — vê apenas dados necessários para o encaminhamento clínico;</li>
            <li><strong>Clínica</strong> — vê apenas o essencial para a reserva/admissão;</li>
            <li><strong>Profissional</strong> — vê o resumo do caso e triagem, <em>sem dados pessoais de contato direto</em> do paciente/família (preservando a intermediação segura);</li>
            <li><strong>Administrador</strong> — vê o conjunto para fins de governança e auditoria hash-chained;</li>
            <li><strong>Família / paciente</strong> — acessa seu próprio dossiê e documentos com autenticação e PIN.</li>
          </ul>
        </Section>

        <Section title="5. Medidas técnicas e organizacionais">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Criptografia em trânsito (HTTPS/TLS 1.2+) em todas as comunicações;</li>
            <li>Armazenamento em ambiente lógico controlado com backups redundantes;</li>
            <li>Autenticação obrigatória por senha forte e 2FA para operadores;</li>
            <li>Registro de auditoria imutável (hash-chained, LGPD Art. 46);</li>
            <li>Salas de vídeo (JaaS 8x8) criptografadas ponta-a-ponta, sem gravação automática;</li>
            <li>Rotação periódica de credenciais e revisão trimestral de acessos.</li>
          </ul>
        </Section>

        <Section title="6. Compartilhamento restrito">
          <p>
            Dados sensíveis só serão compartilhados com terceiros nas seguintes hipóteses, sempre
            respeitando o mínimo necessário:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Prestação do serviço solicitado (clínicas e profissionais credenciados atribuídos ao caso);</li>
            <li>Cumprimento de obrigação legal, regulatória ou judicial;</li>
            <li>Proteção da vida ou incolumidade física do titular ou de terceiros;</li>
            <li>Exercício regular de direitos em processo administrativo ou judicial;</li>
            <li>Consentimento específico e destacado do titular para uma finalidade determinada.</li>
          </ul>
          <p>Em nenhuma hipótese vendemos, cedemos comercialmente ou publicizamos dados sensíveis.</p>
        </Section>

        <Section title="7. Consultas por vídeo, gravação e transcrição">
          <p>
            <strong>Sessões de vídeo NÃO são gravadas por padrão.</strong> Se, no futuro, houver necessidade
            de gravação (finalidade clínica, prova em auditoria, treinamento supervisionado), o profissional
            solicitará <strong>consentimento específico e destacado</strong>, antes do início da sessão,
            com opção clara de recusa sem prejuízo ao atendimento. Resumos textuais elaborados pelo
            profissional após a consulta ficam restritos ao paciente/família responsável e ao próprio
            profissional, sob os mesmos deveres deste termo.
          </p>
        </Section>

        <Section title="8. Encaminhamento a autoridades">
          <p>
            Nos limites da lei, comunicaremos autoridades competentes em situações de <strong>risco iminente
            de vida</strong>, suspeita de violência, abuso ou situação enquadrada como notificação
            compulsória. Sempre que possível, o titular será previamente informado.
          </p>
        </Section>

        <Section title="9. Sanções por violação">
          <p>
            Qualquer colaborador, consultor, clínica ou profissional que descumprir este Termo estará
            sujeito a: (a) descredenciamento imediato, (b) responsabilização civil e criminal aplicável,
            (c) comunicação ao respectivo conselho de classe, (d) reparação integral dos danos causados.
          </p>
        </Section>

        <Section title="10. Direitos do titular">
          <p>
            Você pode, a qualquer momento, acessar, corrigir, anonimizar, portar ou solicitar a eliminação
            dos seus dados através da página <Link to="/meus-dados" className="text-[#0B2B62] underline">
            /meus-dados</Link>, com base no Art. 18 da LGPD.
          </p>
        </Section>

        <Section title="11. Contato do Encarregado (DPO)">
          <p>
            Todas as questões relativas a este Termo devem ser dirigidas ao Encarregado pelo Tratamento de
            Dados Pessoais da RAF Direct, pelo e-mail
            {" "}<a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">contato@rafdirect.com.br</a>.
          </p>
        </Section>

        <div className="mt-12 p-5 rounded-2xl border border-slate-200 bg-slate-50 flex items-start gap-3">
          <EyeOff className="h-5 w-5 text-slate-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 leading-relaxed">
            Ao utilizar a RAF Direct você reconhece ter lido, compreendido e concorda com este Termo de
            Confidencialidade, que integra os
            {" "}<Link to="/termos-de-uso" className="text-[#0B2B62] underline">Termos de Uso</Link> e a
            {" "}<Link to="/politica-de-privacidade" className="text-[#0B2B62] underline">Política de Privacidade</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}
